import type { CashMovement, CashSession } from '@iobiz/shared'
import { dbFor, type LocalRow } from '../db.js'

/**
 * Sessions de caisse, lues dans la RÉPLIQUE.
 *
 * ⭐ Pourquoi la réplique et pas le serveur : le verrou « pas de vente sans
 * caisse ouverte » interroge cet état à chaque encaissement. Le demander au
 * réseau ferait échouer le verrou à la première coupure — il bloquerait donc
 * la vente précisément là où le produit promet de tenir. L'appareil doit
 * savoir seul si le tiroir est ouvert.
 *
 * ⚠️ Lecture SEULE. Ouvrir, clôturer et mouvementer passent par l'API (voir
 * `caisse.write.ts`) : ce sont des gestes rares, tenus par un responsable, et
 * l'unicité de la caisse ouverte est garantie par un index en base qu'aucun
 * appareil ne peut arbitrer tout seul.
 */

/**
 * Les totaux d'une session, RECALCULÉS SUR L'APPAREIL.
 *
 * ⚠️ Ils étaient d'abord projetés par le serveur, dans la ligne de session.
 * C'était faux, et le défaut se voyait tout de suite : enregistrer un apport
 * ne modifie pas la ligne `cash_sessions`, donc rien ne la fait redescendre,
 * donc le total restait à zéro alors que le mouvement s'affichait juste en
 * dessous. Les ventes en espèces avaient exactement le même sort — une
 * journée entière d'encaissements pour un tiroir annoncé vide.
 *
 * ⭐ La réplique détient déjà tout ce qu'il faut : les mouvements du tiroir et
 * les ventes qui lui sont rattachées. Autant les compter ici, où ils sont
 * toujours à jour, plutôt que de faire dépendre un chiffre du hasard d'une
 * autre écriture.
 */
async function totauxDeSession(
  storeId: string,
  sessionId: string,
): Promise<{ cashTotal: number; salesCount: number; movementsTotal: number }> {
  const db = dbFor(storeId)
  const [mouvements, ventes] = await Promise.all([
    db.cashMovements.where('session_id').equals(sessionId).toArray(),
    db.transactions.where('session_id').equals(sessionId).toArray(),
  ])

  const movementsTotal = mouvements.reduce((s, m) => s + Number(m['amount'] ?? 0), 0)

  // ⚠️ Les ESPÈCES seulement : une carte ne passe pas par le tiroir, et la
  // compter ferait apparaître un manque égal au montant encaissé par terminal.
  let cashTotal = 0
  for (const v of ventes) {
    const reglements = (v['payments'] as { method?: string; amount?: number }[] | undefined) ?? []
    for (const p of reglements) {
      if (p.method === 'cash') cashTotal += Number(p.amount ?? 0)
    }
  }

  return { cashTotal, salesCount: ventes.length, movementsTotal }
}

function versSession(r: LocalRow): CashSession {
  return {
    id: String(r['id']),
    openedAt: String(r['opened_at']),
    openedByName: (r['opened_by_name'] as string | null) ?? null,
    openingFloat: Number(r['opening_float'] ?? 0),
    closedAt: (r['closed_at'] as string | null) ?? null,
    closingCounted: r['closing_counted'] === null ? null : Number(r['closing_counted']),
    expectedAmount: r['expected_amount'] === null ? null : Number(r['expected_amount']),
    variance: r['variance'] === null ? null : Number(r['variance']),
    status: (r['status'] as 'open' | 'closed') ?? 'closed',
    notes: (r['notes'] as string | null) ?? null,
    // Valeurs de repli : `totauxDeSession` les remplace à la lecture. Elles
    // servent le temps d'un rendu, et pour une session dont les ventes ne sont
    // pas encore descendues.
    cashTotal: Number(r['cash_total'] ?? 0),
    salesCount: Number(r['sales_count'] ?? 0),
    movementsTotal: Number(r['movements_total'] ?? 0),
  }
}

function versMouvement(r: LocalRow): CashMovement {
  return {
    id: String(r['id']),
    sessionId: String(r['session_id']),
    amount: Number(r['amount'] ?? 0),
    reason: String(r['reason'] ?? ''),
    notes: (r['notes'] as string | null) ?? null,
    createdByName: (r['created_by_name'] as string | null) ?? null,
    createdAt: String(r['created_at']),
  }
}

/** Recolle les totaux recalculés sur une session lue de la réplique. */
async function avecTotaux(storeId: string, session: CashSession): Promise<CashSession> {
  /**
   * ⚠️ Une session CLÔTURÉE garde ses chiffres tels que le serveur les a
   * figés. Les recalculer ferait bouger un écart déjà constaté et signé dès
   * qu'une vente hors ligne remonterait — c'est précisément ce que le gel de
   * `expected_amount` existe pour empêcher.
   */
  if (session.status !== 'open') return session
  return { ...session, ...(await totauxDeSession(storeId, session.id)) }
}

/**
 * La caisse ouverte, s'il y en a une. Au plus une par boutique (index unique).
 *
 * ⭐ La lecture touche les trois tables — sessions, ventes, mouvements — et
 * c'est VOULU : `useLiveQuery` surveille tout ce que la requête lit, si bien
 * qu'un apport enregistré rafraîchit le total tout seul. C'est ce qui manquait
 * quand les totaux venaient du serveur.
 */
export async function sessionOuverte(storeId: string): Promise<CashSession | null> {
  const rows = await dbFor(storeId).cashSessions.where('status').equals('open').toArray()
  return rows[0] ? avecTotaux(storeId, versSession(rows[0])) : null
}

export async function lireSession(storeId: string, id: string): Promise<CashSession | undefined> {
  const row = await dbFor(storeId).cashSessions.get(id)
  return row ? avecTotaux(storeId, versSession(row)) : undefined
}

export async function historiqueSessions(
  storeId: string,
  limite = 30,
): Promise<CashSession[]> {
  const rows = await dbFor(storeId).cashSessions.toArray()
  const sessions = rows
    .map(versSession)
    .sort((a, b) => b.openedAt.localeCompare(a.openedAt))
    .slice(0, limite)
  return Promise.all(sessions.map((s) => avecTotaux(storeId, s)))
}

export async function mouvementsDeSession(
  storeId: string,
  sessionId: string,
): Promise<CashMovement[]> {
  const rows = await dbFor(storeId).cashMovements
    .where('session_id')
    .equals(sessionId)
    .toArray()
  return rows.map(versMouvement).sort((a, b) => a.createdAt.localeCompare(b.createdAt))
}
